import React from 'react';
import { Input } from '../../../../../components/ui/input';
import { X, Equal } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux';
import { formatCashAmount, getCurrencySymbol } from '../../../../../lib/utils';
import { useTranslation } from 'react-i18next';
import { GenericNote } from '../../../../../components/elements/GenericNote';

type Props = {
  denom: Denom;
  onChangeInput: (coupure: CoupureType, qty: number) => void;
  coupures: CoupureInput[] | null;
  bulk?: boolean;
  currency?: string;
};

const Notes: React.FC<Props> = ({ denom, onChangeInput, coupures, bulk = false, currency = 'EUR' }) => {
  const symbol = getCurrencySymbol(currency);
  const { t } = useTranslation();
  const coupureConfig = useSelector((state: RootState) => state.coupure);

  const filterNotes = (notes: Coin[]) => {
    const filtered = notes
      .filter(note =>
        coupureConfig.some(cfg => cfg.order === true && cfg.cn === 'N' && cfg.value === note.coupure)
      )
      .sort((a, b) => b.coupure - a.coupure); // 500 → 5
    console.log('[PlaceOrder][Notes] filter against coupureConfig', {
      input_count: notes.length,
      input_coupures: notes.map(n => n.coupure),
      filtered_count: filtered.length,
      filtered_coupures: filtered.map(n => n.coupure),
      coupureConfig_N_order_true: coupureConfig.filter(c => c.cn === 'N' && c.order === true).map(c => c.value),
    });
    return filtered;
  };

  const calculateSubtotal = (value: number, qty: number) => value * qty;

  const calculateTotal = () => {
    const total =
      coupures
        ?.filter(i => parseFloat(i.coupure) > 2)
        .reduce((sum, i) => sum + i.qty * parseFloat(i.coupure), 0) || 0;
    return Math.round(total * 100) / 100;
  };

  const calculateWeight = () => {
    const total =
      coupures
        ?.filter(i => parseFloat(i.coupure) > 2)
        .reduce((sum, i) => {
          const weight = coupureConfig.find(c => c.value === parseFloat(i.coupure))?.weight || 0;
          return sum + i.qty * weight;
        }, 0) || 0;
    return Math.round(total * 1000) / 1000;
  };

  return (
    <>
      <div className="flex flex-col gap-8">
        {filterNotes(denom.notes as Coin[]).map(coup => {
          const inputQty = coupures?.find(i => parseFloat(i.coupure) === coup.coupure)?.qty || 0;
          const displayValue = inputQty ? Math.round(inputQty / coup.perqty) : '';

          const maxAllowed = bulk
            ? coup.maxqty * (coupureConfig.find(c => c.value === coup.coupure)?.unit || 1)
            : coup.maxqty;

          const handleChange = (val: number) => {
            const clamped = Math.max(coup.minqty, Math.min(maxAllowed, val || 0));
            onChangeInput(coup.coupure.toString() as CoupureType, clamped * coup.perqty);
          };

          return (
            <div key={coup.coupure} className="flex justify-between gap-4 md:grid md:grid-cols-3">
              <div className="flex items-center gap-3">
                <GenericNote value={coup.coupure} currency={currency} className="w-16 h-8" />
                <div>
                  <p className="font-semibold leading-none">
                    {formatCashAmount(coup.coupure)} {currency}
                  </p>
                  {!bulk && (
                    <p className="text-xs sm:text-sm text-neutral-500">
                      {t('order_and_pickup.o_place_order.lias_per')} {coup.perqty}{' '}
                      {t('order_and_pickup.o_place_order.pieces')}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <div className="flex flex-col justify-center items-end min-w-[80px] text-sm">
                  <p className="leading-none">min: <span className="font-semibold">{coup.minqty}</span></p>
                  <p className="leading-none">max: <span className="font-semibold">{maxAllowed}</span></p>
                </div>
                <Input
                  disabled={coup.perqty === 0}
                  value={displayValue}
                  onChange={e => handleChange(Math.round(parseFloat(e.target.value) || 0))}
                  type="number"
                  integerOnly
                  className="px-1 sm:px-3 md:min-w-[100px]"
                  min={coup.minqty}
                  max={maxAllowed}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="hidden gap-2 md:flex">
                  <X className="w-4" strokeWidth={1} />
                  <p className="font-medium">{symbol} {formatCashAmount(coup.coupure * coup.perqty)}</p>
                  <Equal className="w-4" strokeWidth={1} />
                </div>
                <p className="font-semibold">
                  {symbol} {formatCashAmount(calculateSubtotal(coup.coupure, inputQty))}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="w-full h-px bg-neutral-300 my-6" />

      <div className="flex justify-between font-semibold text-lg">
        <p>{t('order_and_pickup.o_place_order.total')}:</p>
        <div className="flex flex-col items-end">
          <p>{symbol} {formatCashAmount(calculateTotal())}</p>
          <p className="text-sm text-neutral-600">{calculateWeight()} kg</p>
        </div>
      </div>
    </>
  );
};

export default Notes;
